import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Table } from "../../../components/SystemComponent/table";
import { GroupButton } from "../../../components/SystemComponent/GroupButton";
import { Badge } from "../../../components/SystemComponent/Bagde";
import { Modal } from "../../../components/SystemComponent/Modal";
import {
  MdVisibility,
  MdCheck,
  MdClose,
  MdFlight,
  MdInfo,
  MdSettings,
  MdImage,
  MdPictureAsPdf,
  MdDescription,
  MdPerson,
  MdBusiness,
} from "react-icons/md";
import { createApi } from "../../../../core/api";

const ConfirmStatusText: any = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const ConfirmStatusColor: any = { 0: "#F59E0B", 1: "#16A34A", 2: "#DC2626" };

const BoolBadge = ({ value }: any) => (
  <Badge
    label={value ? "Yes" : "No"}
    color={value ? "#16A34A" : "#DC2626"}
    bg={value ? "rgba(22,163,74,.15)" : "rgba(220,38,38,.15)"}
  />
);

const InfoRow = ({ icon: Icon, label, value }: any) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "28px 200px 1fr",
      alignItems: "center",
      gap: 10,
      padding: "6px 0",
      borderBottom: "1px solid #E5E7EB",
      fontSize: 12,
    }}
  >
    <Icon size={22} color="#25b3ebff" />
    <span style={{ color: "#64748B", fontWeight: 600 }}>{label}</span>
    <span style={{ fontWeight: 600 }}>{value ?? "-"}</span>
  </div>
);

const Card = ({ icon: Icon, title, children }: any) => (
  <div
    style={{
      border: "1px solid #E5E7EB",
      borderRadius: 18,
      padding: 16,
      background: "#FFFFFF",
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        background:
          "linear-gradient(135deg, #3B82F6 0%, #60A5FA 50%, #93C5FD 100%)",
        color: "#fff",
        borderRadius: 8,
        padding: "6px 10px",
      }}
    >
      <Icon size={20} />
      <strong style={{ fontSize: 14 }}>{title}</strong>
    </div>
    {children}
  </div>
);

const Loader = () => (
  <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
    <div
      style={{
        width: 42,
        height: 42,
        border: "4px solid #CBD5E1",
        borderTopColor: "#2563EB",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
  </div>
);

export const AircraftRequestDetailModal = ({ id, onClose }: any) => {
  const api = useMemo(() => createApi(), []);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    api.aircraftRequest.getById(id).then((r: any) => {
      setData(r?.data?.data ?? null);
      setLoading(false);
    });
  }, [id]);

  if (loading)
    return (
      <Modal open fullScreen onClose={onClose}>
        <Loader />
      </Modal>
    );

  if (!data)
    return (
      <Modal open onClose={onClose}>
        <div style={{ padding: 20 }}>No data</div>
      </Modal>
    );

  const COMMON_KEYS = new Set([
    "headline",
    "description",
    "confirmStatus",
    "isActive",
    "images",
    "documents",
    "location",
    "user",
    "company",
    "aircraftType",
    "aircraftTypeModel",
    "engineType",
    "airport",
    "requestDescripton",
    "createdDate",
  ]);

  const isValidDateString = (value: string) => {
    const d = new Date(value);
    return !isNaN(d.getTime());
  };

  const renderExtraValue = (v: any) => {
    if (v === null || v === undefined) return "-";

    if (typeof v === "boolean") return <BoolBadge value={v} />;

    if (typeof v === "number") return v;

    if (typeof v === "string") {
      if (isValidDateString(v)) return new Date(v).toLocaleDateString();
      return v;
    }

    if (typeof v === "object") {
      if (v.name) return v.name;
      if (v.code) return v.code;
      if (v.codeIATA) return v.codeIATA;
      return "-";
    }

    return "-";
  };

  return (
    <Modal open fullScreen onClose={onClose}>
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 22, marginBottom: 24 }}>
          {data.requestDescripton}
        </h1>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(420px,1fr))",
            gap: 20,
          }}
        >
          <Card icon={MdDescription} title="General">
            <InfoRow
              icon={MdDescription}
              label="Request"
              value={data.requestDescripton}
            />
            <InfoRow
              icon={MdInfo}
              label="Confirm Status"
              value={
                <Badge
                  label={ConfirmStatusText[data.confirmStatus]}
                  color={ConfirmStatusColor[data.confirmStatus]}
                />
              }
            />
            <InfoRow
              icon={MdSettings}
              label="Is Active"
              value={<BoolBadge value={data.isActive} />}
            />
            <InfoRow
              icon={MdInfo}
              label="Created Date"
              value={new Date(data.createdDate).toLocaleDateString()}
            />
          </Card>
          <Card icon={MdInfo} title="Additional Information">
            {Object.entries(data.data)
              .filter(([k]) => !COMMON_KEYS.has(k))
              .map(([k, v]) => (
                <InfoRow
                  key={k}
                  icon={MdInfo}
                  label={k.replace(/([a-z])([A-Z])/g, "$1 $2").toUpperCase()}
                  value={renderExtraValue(v)}
                />
              ))}
          </Card>

          {data.data.aircraftType && (
            <Card icon={MdFlight} title="Aircraft">
              <InfoRow
                icon={MdFlight}
                label="Aircraft Type"
                value={data.data.aircraftType?.name}
              />
              <InfoRow
                icon={MdFlight}
                label="Aircraft Model"
                value={data.data.aircraftTypeModel?.name}
              />
            </Card>
          )}

          {data.data.engineType && (
            <Card icon={MdSettings} title="Engine">
              <InfoRow
                icon={MdSettings}
                label="Engine Type"
                value={data.data.engineType?.code}
              />
            </Card>
          )}

          {data.data.user && (
            <Card icon={MdPerson} title="User">
              <InfoRow
                icon={MdPerson}
                label="Full Name"
                value={`${data.data.user.firstName} ${data.data.user.lastName}`}
              />
              <InfoRow
                icon={MdInfo}
                label="Email"
                value={data.data.user.email}
              />
              <InfoRow
                icon={MdInfo}
                label="Phone"
                value={data.data.user.phoneNumber}
              />
            </Card>
          )}

          {data.data.company && (
            <Card icon={MdBusiness} title="Company">
              <InfoRow
                icon={MdBusiness}
                label="Company Name"
                value={data.data.company.companyName}
              />
              <InfoRow
                icon={MdInfo}
                label="Phone"
                value={data.data.company.phone}
              />
              <InfoRow
                icon={MdInfo}
                label="Email"
                value={data.data.company.email}
              />
            </Card>
          )}

          {data.data.location && (
            <Card icon={MdInfo} title="Location">
              <InfoRow
                icon={MdInfo}
                label="Country"
                value={data.data.location.country?.name}
              />
              <InfoRow
                icon={MdInfo}
                label="City"
                value={data.data.location.city?.name}
              />
              <InfoRow
                icon={MdFlight}
                label="Airport"
                value={
                  data.data.location.airport
                    ? `${data.data.location.airport.name} (${data.data.location.airport.codeIATA})`
                    : "-"
                }
              />
            </Card>
          )}
        </div>

        <Card icon={MdImage} title="Images">
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {(data.images ?? []).length === 0 && <span>-</span>}
            {(data.images ?? []).map((m: any) => (
              <img
                key={m.id}
                src={m.url}
                onClick={() => setPreview(m.url)}
                style={{
                  width: 140,
                  height: 140,
                  objectFit: "cover",
                  borderRadius: 14,
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </Card>

        <Card icon={MdPictureAsPdf} title="Documents">
          <div style={{ display: "flex", gap: 16 }}>
            {(data.documents ?? []).length === 0 && <span>-</span>}
            {(data.documents ?? []).map((m: any) => (
              <div
                key={m.id}
                onClick={() => setPreview(m.url)}
                style={{
                  width: 80,
                  height: 80,
                  borderRadius: 14,
                  border: "1px solid #E5E7EB",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <MdPictureAsPdf size={36} />
              </div>
            ))}
          </div>
        </Card>

        {preview && (
          <Modal open onClose={() => setPreview(null)}>
            {preview.endsWith(".pdf") ? (
              <iframe src={preview} style={{ width: "100%", height: "80vh" }} />
            ) : (
              <img src={preview} style={{ maxWidth: "100%" }} />
            )}
          </Modal>
        )}
      </div>
    </Modal>
  );
};
